import { useEffect, useState } from "react";
import { api, asArray } from "@/lib/api";
import { toast } from "sonner";
import { Building2, Plus, Pencil, Trash2, ShieldCheck, X, Server, Save, KeyRound, Layers, Rocket, Check } from "lucide-react";
import DrillPanel from "@/components/DrillPanel";

// Full client management: create / edit / delete clients, and edit each client's
// policy (device control, backup, updates) — the policy that its enrolled agents
// inherit. Devices are grouped by client here; deleting a client unassigns its
// agents rather than deleting them.

const EMPTY = { name: "", industry: "", country: "", tier: "Standard", primary_contact_name: "" };
const DEFAULT_POLICY = {
  device: { usb_storage_enabled: true, printscreen_enabled: true },
  backup: { enabled: false, paths: [], keep: 5, schedule_hours: 24, upload_url: "" },
  update: { auto_install: false, severities: ["critical", "important"], schedule_hours: 24 },
};

function Modal({ title, onClose, children, wide }) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4" onClick={onClose}>
      <div onClick={e => e.stopPropagation()}
        className={`bg-zinc-950 border border-zinc-800 rounded-lg shadow-2xl w-full ${wide ? "max-w-lg" : "max-w-md"} max-h-[90vh] overflow-y-auto`}>
        <div className="flex items-center justify-between px-5 py-3.5 border-b border-zinc-900 sticky top-0 bg-zinc-950">
          <div className="text-sm font-medium text-zinc-100">{title}</div>
          <button onClick={onClose} className="text-zinc-500 hover:text-zinc-200"><X size={16} /></button>
        </div>
        <div className="p-5">{children}</div>
      </div>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div className="mb-3">
      <div className="text-[10px] uppercase tracking-widest text-zinc-500 font-mono mb-1.5">{label}</div>
      {children}
    </div>
  );
}

const inputCls = "w-full bg-zinc-900 border border-zinc-800 rounded px-3 py-2 text-sm text-zinc-200 focus:border-zinc-600 outline-none";

function Toggle({ on, onChange, label, hint }) {
  return (
    <div className="flex items-center justify-between py-2">
      <div>
        <div className="text-sm text-zinc-200">{label}</div>
        {hint && <div className="text-[11px] text-zinc-600">{hint}</div>}
      </div>
      <button onClick={() => onChange(!on)}
        className={`w-10 h-5.5 rounded-full transition-colors relative shrink-0 ${on ? "bg-emerald-500/70" : "bg-zinc-700"}`}
        style={{ height: 22, width: 40 }}>
        <span className={`absolute top-0.5 w-[18px] h-[18px] rounded-full bg-white transition-all ${on ? "left-[20px]" : "left-0.5"}`} />
      </button>
    </div>
  );
}

export default function Clients() {
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [edit, setEdit] = useState(null);       // {mode:'create'|'edit', data}
  const [policyFor, setPolicyFor] = useState(null); // client
  const [policy, setPolicy] = useState(null);
  const [credFor, setCredFor] = useState(null);     // client
  const [cred, setCred] = useState({ configured: false, username: "", domain: ".", password: "" });
  const [drill, setDrill] = useState(null);         // client id whose devices to show
  const [bundleFor, setBundleFor] = useState(null); // client whose bundles we manage
  const [bundles, setBundles] = useState([]);
  const [catalog, setCatalog] = useState([]);       // package catalogue
  const [bEdit, setBEdit] = useState(null);         // {id?, name, package_ids:[]} draft, or null
  const [saving, setSaving] = useState(false);

  const load = () => api.get("/clients").then(r => { setClients(asArray(r.data)); setLoading(false); }).catch(() => setLoading(false));
  useEffect(() => { load(); }, []);

  const openBundles = async (c) => {
    setBundleFor(c); setBundles([]); setBEdit(null);
    try {
      const [b, p] = await Promise.all([
        api.get(`/clients/${c.id}/bundles`),
        api.get("/packages"),
      ]);
      setBundles(asArray(b.data)); setCatalog(asArray(p.data));
    } catch { toast.error("Failed to load bundles"); }
  };
  const reloadBundles = async () => {
    try { const { data } = await api.get(`/clients/${bundleFor.id}/bundles`); setBundles(asArray(data)); } catch {}
  };
  const saveBundle = async () => {
    if (!bEdit.name?.trim()) { toast.error("Bundle name required"); return; }
    setSaving(true);
    try {
      if (bEdit.id) await api.put(`/bundles/${bEdit.id}`, { name: bEdit.name, package_ids: bEdit.package_ids });
      else await api.post(`/clients/${bundleFor.id}/bundles`, { name: bEdit.name, package_ids: bEdit.package_ids });
      toast.success("Bundle saved"); setBEdit(null); await reloadBundles();
    } catch (e) { toast.error(e?.response?.data?.detail || "Save failed"); } finally { setSaving(false); }
  };
  const deleteBundle = async (b) => {
    if (!window.confirm(`Delete bundle "${b.name}"?`)) return;
    try { await api.delete(`/bundles/${b.id}`); toast.success("Bundle deleted"); await reloadBundles(); } catch { toast.error("Failed"); }
  };
  const deployBundle = async (b) => {
    if (!window.confirm(`Deploy "${b.name}" (${(b.packages || []).length} apps) silently to ALL of ${bundleFor.name}'s devices?`)) return;
    try {
      const { data } = await api.post(`/bundles/${b.id}/deploy`, {});
      toast.success(`Queued ${data.queued} install(s) across ${data.devices} device(s)`);
    } catch (e) { toast.error(e?.response?.data?.detail || "Deploy failed"); }
  };
  const toggleBundlePkg = (pid) => setBEdit(s => {
    const has = s.package_ids.includes(pid);
    return { ...s, package_ids: has ? s.package_ids.filter(x => x !== pid) : [...s.package_ids, pid] };
  });

  const openCred = async (c) => {
    setCredFor(c); setCred({ configured: false, username: "", domain: ".", password: "" });
    try { const { data } = await api.get(`/clients/${c.id}/deploy-credential`); setCred({ ...data, password: "", domain: data.domain || "." }); } catch {}
  };
  const saveCred = async () => {
    if (!cred.username.trim() || !cred.password.trim()) { toast.error("Username and password required"); return; }
    setSaving(true);
    try { await api.put(`/clients/${credFor.id}/deploy-credential`, { username: cred.username, domain: cred.domain || ".", password: cred.password }); toast.success("Deployment credential saved (encrypted)"); setCredFor(null); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); } finally { setSaving(false); }
  };
  const clearCred = async () => {
    if (!window.confirm("Remove the deployment credential for this client?")) return;
    try { await api.delete(`/clients/${credFor.id}/deploy-credential`); toast.success("Credential removed"); setCredFor(null); } catch { toast.error("Failed"); }
  };

  const openCreate = () => setEdit({ mode: "create", data: { ...EMPTY } });
  const openEdit = (c) => setEdit({ mode: "edit", data: { ...EMPTY, ...c } });

  const saveClient = async () => {
    const d = edit.data;
    if (!d.name?.trim()) { toast.error("Name is required"); return; }
    setSaving(true);
    try {
      if (edit.mode === "create") {
        await api.post("/clients", d);
        toast.success("Client created");
      } else {
        await api.patch(`/clients/${d.id}`, {
          name: d.name, industry: d.industry, country: d.country, tier: d.tier, primary_contact_name: d.primary_contact_name,
        });
        toast.success("Client updated");
      }
      setEdit(null); await load();
    } catch { toast.error("Save failed"); } finally { setSaving(false); }
  };

  const removeClient = async (c) => {
    if (!window.confirm(`Delete "${c.name}"? Its ${c.agent_count || 0} agent(s) will be unassigned (not removed).`)) return;
    try {
      const { data } = await api.delete(`/clients/${c.id}`);
      toast.success(`Deleted — ${data.agents_unassigned} agent(s) unassigned`);
      await load();
    } catch { toast.error("Delete failed"); }
  };

  const openPolicy = async (c) => {
    setPolicyFor(c); setPolicy(null);
    try {
      const { data } = await api.get(`/clients/${c.id}/policy`);
      setPolicy({ ...DEFAULT_POLICY, ...data, device: { ...DEFAULT_POLICY.device, ...data.device },
        backup: { ...DEFAULT_POLICY.backup, ...data.backup }, update: { ...DEFAULT_POLICY.update, ...data.update } });
    } catch { setPolicy({ ...DEFAULT_POLICY }); }
  };

  const savePolicy = async () => {
    setSaving(true);
    try {
      const { data } = await api.put(`/clients/${policyFor.id}/policy`, policy);
      toast.success(`Policy saved · pushed to ${data.pushed_to} online agent(s)`);
      setPolicyFor(null); await load();
    } catch { toast.error("Policy save failed"); } finally { setSaving(false); }
  };

  const setDev = (k, v) => setPolicy(p => ({ ...p, device: { ...p.device, [k]: v } }));
  const setBk = (k, v) => setPolicy(p => ({ ...p, backup: { ...p.backup, [k]: v } }));
  const setUp = (k, v) => setPolicy(p => ({ ...p, update: { ...p.update, [k]: v } }));
  const toggleSev = (s) => setPolicy(p => {
    const has = p.update.severities.includes(s);
    return { ...p, update: { ...p.update, severities: has ? p.update.severities.filter(x => x !== s) : [...p.update.severities, s] } };
  });

  return (
    <div className="p-6 md:p-8 max-w-[1200px] mx-auto">
      <div className="flex items-center justify-between flex-wrap gap-3 mb-6">
        <div className="flex items-center gap-3">
          <div className="w-9 h-9 rounded-lg bg-blue-500/10 border border-blue-500/30 flex items-center justify-center">
            <Building2 className="text-blue-400" size={18} />
          </div>
          <div>
            <h1 className="text-xl font-semibold text-zinc-50">Clients</h1>
            <div className="text-xs text-zinc-500 uppercase tracking-widest font-mono">Manage clients &amp; per-client policy</div>
          </div>
        </div>
        <button onClick={openCreate} data-testid="new-client"
          className="inline-flex items-center gap-2 bg-blue-500/15 hover:bg-blue-500/25 border border-blue-500/40 text-blue-200 rounded-md px-4 py-2 text-sm">
          <Plus size={16} /> New client
        </button>
      </div>

      {loading ? (
        <div className="text-zinc-500 text-sm">Loading…</div>
      ) : clients.length === 0 ? (
        <div className="bg-zinc-950 border border-zinc-900 border-dashed rounded-lg p-10 text-center">
          <Building2 className="mx-auto text-zinc-700 mb-3" size={28} />
          <div className="text-zinc-300 text-sm mb-1">No clients yet</div>
          <div className="text-zinc-600 text-xs mb-4">Create a client, then hand out an installer link so their devices enrol under it.</div>
          <button onClick={openCreate} className="inline-flex items-center gap-2 bg-blue-500/15 border border-blue-500/40 text-blue-200 rounded-md px-4 py-2 text-sm mx-auto"><Plus size={15} /> New client</button>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
          {clients.map(c => (
            <div key={c.id} data-testid={`client-${c.id}`} className="bg-zinc-950 border border-zinc-900 rounded-md p-5 hover:border-zinc-800 transition-colors">
              <div className="flex items-start justify-between">
                <div className="min-w-0">
                  <div className="text-[10px] uppercase tracking-widest text-zinc-500 font-mono">{c.industry || "—"}</div>
                  <h2 className="text-lg font-semibold text-zinc-100 truncate mt-0.5">{c.name}</h2>
                  <div className="text-xs text-zinc-500 mt-0.5">{c.country || "—"}{c.tier ? ` · ${c.tier}` : ""}</div>
                </div>
              </div>
              <div className="flex items-center gap-4 mt-4 text-xs">
                <button onClick={() => setDrill(c.id)} data-testid={`client-devices-${c.id}`}
                  className="flex items-center gap-1.5 text-zinc-400 hover:text-blue-300 disabled:hover:text-zinc-400" disabled={!c.agent_count}>
                  <Server size={13} /> <span className="font-mono text-zinc-200">{c.agent_count || 0}</span> agents{c.agent_count ? " ↗" : ""}
                </button>
                <div className="text-zinc-600">{c.site_count || 0} sites</div>
              </div>
              <div className="flex flex-wrap items-center gap-2 mt-4 pt-3 border-t border-zinc-900">
                <button onClick={() => openPolicy(c)} data-testid={`policy-${c.id}`}
                  className="inline-flex items-center gap-1.5 bg-zinc-900 hover:bg-zinc-800 border border-zinc-800 rounded px-2.5 py-1.5 text-xs text-zinc-300"><ShieldCheck size={13} /> Policy</button>
                <button onClick={() => openBundles(c)} data-testid={`bundles-${c.id}`} title="Software deployment bundles"
                  className="inline-flex items-center gap-1.5 bg-zinc-900 hover:bg-zinc-800 border border-zinc-800 rounded px-2.5 py-1.5 text-xs text-zinc-300"><Layers size={13} /> Bundles</button>
                <button onClick={() => openCred(c)} title="Deployment admin credential"
                  className="inline-flex items-center gap-1.5 bg-zinc-900 hover:bg-zinc-800 border border-zinc-800 rounded px-2.5 py-1.5 text-xs text-zinc-300"><KeyRound size={13} /> Credential</button>
                <button onClick={() => openEdit(c)} className="inline-flex items-center gap-1.5 bg-zinc-900 hover:bg-zinc-800 border border-zinc-800 rounded px-2.5 py-1.5 text-xs text-zinc-300"><Pencil size={13} /> Edit</button>
                <button onClick={() => removeClient(c)} title="Delete client" className="inline-flex items-center gap-1.5 bg-red-500/5 hover:bg-red-500/15 border border-red-500/20 rounded px-2.5 py-1.5 text-xs text-red-400"><Trash2 size={13} /></button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* create / edit */}
      {edit && (
        <Modal title={edit.mode === "create" ? "New client" : "Edit client"} onClose={() => setEdit(null)}>
          <Field label="Name"><input className={inputCls} value={edit.data.name} onChange={e => setEdit(s => ({ ...s, data: { ...s.data, name: e.target.value } }))} autoFocus /></Field>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Industry"><input className={inputCls} value={edit.data.industry} onChange={e => setEdit(s => ({ ...s, data: { ...s.data, industry: e.target.value } }))} /></Field>
            <Field label="Country"><input className={inputCls} value={edit.data.country} onChange={e => setEdit(s => ({ ...s, data: { ...s.data, country: e.target.value } }))} /></Field>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Tier">
              <select className={inputCls} value={edit.data.tier} onChange={e => setEdit(s => ({ ...s, data: { ...s.data, tier: e.target.value } }))}>
                <option>Standard</option><option>Premium</option><option>Enterprise</option>
              </select>
              <div className="text-[11px] text-zinc-500 mt-1">
                Sets this client's default SLA targets (Service Desk Config → SLA), unless
                the client has its own override.
              </div>
            </Field>
            <Field label="Primary contact"><input className={inputCls} value={edit.data.primary_contact_name} onChange={e => setEdit(s => ({ ...s, data: { ...s.data, primary_contact_name: e.target.value } }))} /></Field>
          </div>
          <div className="flex justify-end gap-2 mt-4">
            <button onClick={() => setEdit(null)} className="px-3.5 py-2 text-sm text-zinc-400 hover:text-zinc-200">Cancel</button>
            <button onClick={saveClient} disabled={saving} className="inline-flex items-center gap-2 bg-blue-500/15 border border-blue-500/40 text-blue-200 rounded-md px-4 py-2 text-sm disabled:opacity-50"><Save size={15} /> {saving ? "Saving…" : "Save"}</button>
          </div>
        </Modal>
      )}

      {/* policy editor */}
      {policyFor && (
        <Modal title={`Policy · ${policyFor.name}`} onClose={() => setPolicyFor(null)} wide>
          {!policy ? <div className="text-zinc-500 text-sm">Loading policy…</div> : (
            <>
              <div className="text-[10px] uppercase tracking-widest text-zinc-500 font-mono mb-1">Device control</div>
              <div className="bg-zinc-900/30 border border-zinc-900 rounded p-3 mb-4">
                <Toggle on={policy.device.usb_storage_enabled} onChange={v => setDev("usb_storage_enabled", v)} label="Allow USB storage" hint="Off = block USB mass storage on this client's devices" />
                <Toggle on={policy.device.printscreen_enabled} onChange={v => setDev("printscreen_enabled", v)} label="Allow Print Screen" hint="Off = disable screenshot capture" />
              </div>

              <div className="text-[10px] uppercase tracking-widest text-zinc-500 font-mono mb-1">Backup</div>
              <div className="bg-zinc-900/30 border border-zinc-900 rounded p-3 mb-4">
                <Toggle on={policy.backup.enabled} onChange={v => setBk("enabled", v)} label="Scheduled endpoint backup" />
                {policy.backup.enabled && (
                  <div className="mt-2 space-y-2">
                    <input className={inputCls} placeholder="Folders, comma-separated (e.g. C:/Users, C:/Data)"
                      value={(policy.backup.paths || []).join(", ")}
                      onChange={e => setBk("paths", e.target.value.split(",").map(x => x.trim()).filter(Boolean))} />
                    <div className="grid grid-cols-2 gap-2">
                      <label className="text-xs text-zinc-500">Every (hours)<input type="number" className={inputCls + " mt-1"} value={policy.backup.schedule_hours} onChange={e => setBk("schedule_hours", +e.target.value)} /></label>
                      <label className="text-xs text-zinc-500">Keep versions<input type="number" className={inputCls + " mt-1"} value={policy.backup.keep} onChange={e => setBk("keep", +e.target.value)} /></label>
                    </div>
                  </div>
                )}
              </div>

              <div className="text-[10px] uppercase tracking-widest text-zinc-500 font-mono mb-1">Updates</div>
              <div className="bg-zinc-900/30 border border-zinc-900 rounded p-3 mb-4">
                <Toggle on={policy.update.auto_install} onChange={v => setUp("auto_install", v)} label="Auto-install patches" />
                {policy.update.auto_install && (
                  <div className="mt-2 space-y-2">
                    <div className="flex flex-wrap gap-1.5">
                      {["critical", "important", "moderate", "low"].map(s => (
                        <button key={s} onClick={() => toggleSev(s)}
                          className={`px-2.5 py-1 rounded text-[11px] border ${policy.update.severities.includes(s) ? "bg-blue-500/15 border-blue-500/40 text-blue-200" : "bg-zinc-900 border-zinc-800 text-zinc-500"}`}>{s}</button>
                      ))}
                    </div>
                    <label className="text-xs text-zinc-500 block">Check every (hours)<input type="number" className={inputCls + " mt-1"} value={policy.update.schedule_hours} onChange={e => setUp("schedule_hours", +e.target.value)} /></label>
                  </div>
                )}
              </div>

              <div className="text-[11px] text-zinc-600 mb-3">Saving pushes this policy to the client's online agents immediately; offline agents apply it on next connect.</div>
              <div className="flex justify-end gap-2">
                <button onClick={() => setPolicyFor(null)} className="px-3.5 py-2 text-sm text-zinc-400 hover:text-zinc-200">Cancel</button>
                <button onClick={savePolicy} disabled={saving} className="inline-flex items-center gap-2 bg-emerald-500/15 border border-emerald-500/40 text-emerald-200 rounded-md px-4 py-2 text-sm disabled:opacity-50"><Save size={15} /> {saving ? "Saving…" : "Save & push"}</button>
              </div>
            </>
          )}
        </Modal>
      )}

      {/* deployment credential */}
      {credFor && (
        <Modal title={`Deployment credential · ${credFor.name}`} onClose={() => setCredFor(null)}>
          <div className="text-xs text-zinc-500 mb-3">
            A local-admin account the agent uses to run installs elevated when it is not a SYSTEM service.
            Stored encrypted; never shown again.
          </div>
          {cred.configured && <div className="text-[11px] text-emerald-400/80 mb-3">A credential is currently set{cred.username ? ` (${cred.username})` : ""}. Enter a new one to replace it.</div>}
          <Field label="Username"><input className={inputCls} placeholder="Administrator" value={cred.username} onChange={e => setCred(s => ({ ...s, username: e.target.value }))} /></Field>
          <Field label="Domain (. for local)"><input className={inputCls} value={cred.domain} onChange={e => setCred(s => ({ ...s, domain: e.target.value }))} /></Field>
          <Field label="Password"><input type="password" className={inputCls} value={cred.password} onChange={e => setCred(s => ({ ...s, password: e.target.value }))} /></Field>
          <div className="flex items-start gap-2 text-[11px] text-amber-500/80 mt-1 mb-3">
            <KeyRound size={13} className="shrink-0 mt-0.5" />
            <span>Use the built-in Administrator or a dedicated service account — a normal UAC-filtered admin returns a reduced token and installs to Program Files/HKLM can still be denied. For full reliability, install the agent as the SYSTEM service instead.</span>
          </div>
          <div className="flex justify-between gap-2">
            {cred.configured ? <button onClick={clearCred} className="text-sm text-red-400/80 hover:text-red-300">Remove</button> : <span />}
            <div className="flex gap-2">
              <button onClick={() => setCredFor(null)} className="px-3.5 py-2 text-sm text-zinc-400 hover:text-zinc-200">Cancel</button>
              <button onClick={saveCred} disabled={saving} className="inline-flex items-center gap-2 bg-blue-500/15 border border-blue-500/40 text-blue-200 rounded-md px-4 py-2 text-sm disabled:opacity-50"><Save size={15} /> {saving ? "Saving…" : "Save"}</button>
            </div>
          </div>
        </Modal>
      )}

      {/* deployment bundles */}
      {bundleFor && (
        <Modal title={`Deployment bundles · ${bundleFor.name}`} onClose={() => { setBundleFor(null); setBEdit(null); }} wide>
          <div className="text-xs text-zinc-500 mb-4">
            A bundle is a named set of applications this client's devices should have. Deploy one to install
            every app silently across all of {bundleFor.name}'s devices (uses the client's deployment credential).
          </div>

          {/* existing bundles */}
          {!bEdit && (
            <div className="space-y-2 mb-4">
              {bundles.length === 0 && <div className="text-sm text-zinc-600 bg-zinc-900/30 border border-zinc-900 border-dashed rounded p-4 text-center">No bundles yet.</div>}
              {bundles.map(b => (
                <div key={b.id} className="bg-zinc-900/40 border border-zinc-900 rounded px-3 py-2.5">
                  <div className="flex items-center justify-between gap-2">
                    <div className="min-w-0">
                      <div className="text-sm text-zinc-100 truncate">{b.name}</div>
                      <div className="text-[10px] text-zinc-500 truncate">{(b.packages || []).length} app{(b.packages || []).length === 1 ? "" : "s"}: {(b.packages || []).map(p => p.name).join(", ") || "empty"}</div>
                    </div>
                    <div className="flex items-center gap-1.5 shrink-0">
                      <button onClick={() => deployBundle(b)} disabled={!(b.packages || []).length} title="Deploy to all devices"
                        className="inline-flex items-center gap-1 bg-indigo-600 hover:bg-indigo-700 text-white rounded px-2.5 py-1.5 text-xs disabled:opacity-50"><Rocket size={12} /> Deploy</button>
                      <button onClick={() => setBEdit({ id: b.id, name: b.name, package_ids: (b.packages || []).map(p => p.id) })}
                        className="p-1.5 text-zinc-400 hover:text-zinc-200"><Pencil size={13} /></button>
                      <button onClick={() => deleteBundle(b)} className="p-1.5 text-red-400/70 hover:text-red-300"><Trash2 size={13} /></button>
                    </div>
                  </div>
                </div>
              ))}
              <button onClick={() => setBEdit({ name: "", package_ids: [] })}
                className="w-full inline-flex items-center justify-center gap-2 border border-dashed border-zinc-800 hover:border-zinc-700 rounded px-3 py-2 text-xs text-zinc-400"><Plus size={14} /> New bundle</button>
            </div>
          )}

          {/* bundle editor */}
          {bEdit && (
            <div>
              <Field label="Bundle name"><input className={inputCls} placeholder="e.g. Standard Workstation" value={bEdit.name} onChange={e => setBEdit(s => ({ ...s, name: e.target.value }))} autoFocus /></Field>
              <div className="text-[10px] uppercase tracking-widest text-zinc-500 font-mono mb-1.5">Applications ({bEdit.package_ids.length} selected)</div>
              <div className="max-h-64 overflow-y-auto border border-zinc-900 rounded divide-y divide-zinc-900/60 mb-4">
                {catalog.map(p => {
                  const on = bEdit.package_ids.includes(p.id);
                  return (
                    <button key={p.id} onClick={() => toggleBundlePkg(p.id)}
                      className="w-full flex items-center justify-between gap-2 px-3 py-2 text-left hover:bg-zinc-900/50">
                      <div className="min-w-0">
                        <div className="text-sm text-zinc-200 truncate">{p.name}</div>
                        <div className="text-[10px] text-zinc-600 truncate">{p.publisher || "—"} · {p.method}</div>
                      </div>
                      <span className={`w-4 h-4 rounded border flex items-center justify-center shrink-0 ${on ? "bg-blue-500/80 border-blue-400" : "border-zinc-700"}`}>{on && <Check size={11} className="text-white" />}</span>
                    </button>
                  );
                })}
                {catalog.length === 0 && <div className="px-3 py-4 text-xs text-zinc-600">Catalogue is empty — add packages on the Software page first.</div>}
              </div>
              <div className="flex justify-end gap-2">
                <button onClick={() => setBEdit(null)} className="px-3.5 py-2 text-sm text-zinc-400 hover:text-zinc-200">Cancel</button>
                <button onClick={saveBundle} disabled={saving} className="inline-flex items-center gap-2 bg-blue-500/15 border border-blue-500/40 text-blue-200 rounded-md px-4 py-2 text-sm disabled:opacity-50"><Save size={15} /> {saving ? "Saving…" : "Save bundle"}</button>
              </div>
            </div>
          )}
        </Modal>
      )}

      {drill && <DrillPanel metric="client_devices" client={drill} onClose={() => setDrill(null)} />}
    </div>
  );
}
